import type { ReplayPlugin } from "@slot/client-engine";
import { ChanceDetonationPresenter } from "./ChanceDetonationPresenter";
import type { ChanceMarkersView } from "./ChanceMarkersView";
import type { ChanceRemovalEffect } from "./ChanceRemovalEffect";
import { DetonateChanceSymbolActionHandler, type ChanceDetonationTarget } from "./replay/DetonateChanceSymbolActionHandler";
import { SYMBOL_SPINE_WIN_CLIP } from "../../assets/symbols/symbolSpineCatalog";
import type { GridCells, Log, PresentationTaskRunner, SymbolDimExposurePort, SymbolSpineHost } from "@slot/presentation";
import type { GameFeature } from "@slot/game-shell";

export interface ChanceFeatureDeps {
  grid: GridCells;
  /** A cell's size in the grid's space (the grid layer's computed layout). */
  cellSize: () => { width: number; height: number };
  runner: PresentationTaskRunner;
  /** Hosts the barrel's skeleton; null → no Spine barrel (its cell shrinks and bursts with the targets). */
  symbolSpineHost: SymbolSpineHost | null;
  /** The scene's target markers; null → the targets are not marked. */
  markers: ChanceMarkersView | null;
  /** The tumble's removal effect, through which the barrel's cell leaves with its clip. */
  removalEffect: ChanceRemovalEffect;
  /** The mechanic's removal with its post-removal hold: what a win's `remove_symbols_by_mask` runs. */
  removeCells: (positionMask: string) => Promise<void>;
  /** Per-sprite exposure owner for the dim — the SAME primitive the win highlight uses. */
  dimPort: SymbolDimExposurePort | null;
  playSound: (soundId: string) => void;
  log: Log;
}

/**
 * The chance (CH) detonation on a dead grid: a `ChanceDetonationPresenter` driven by the `detonate_chance_symbol`
 * action, which presents the blast and removes its cells (no `remove_symbols_by_mask` follows it).
 */
export const chanceFeature: GameFeature<ChanceFeatureDeps, ChanceDetonationPresenter, ChanceDetonationTarget> = {
  createPresenter(deps) {
    const host = deps.symbolSpineHost;
    return new ChanceDetonationPresenter({
      gridView: deps.grid,
      runner: deps.runner,
      cellSize: deps.cellSize,
      markers: deps.markers,
      dimPort: deps.dimPort,
      // The explosion: the barrel's own skeleton, resting on the source cell, plays `win` in place.
      playSourceClip: (index) => {
        const view = deps.grid.getSymbolViewAtIndex(index);
        const symbolId = view?.getSymbolId();
        if (!host || !view || !symbolId) return null;
        const seconds = host.play(view, symbolId, SYMBOL_SPINE_WIN_CLIP);
        return seconds > 0 ? seconds : null;
      },
      removalEffect: deps.removalEffect,
      removeCells: deps.removeCells,
      playSound: deps.playSound,
      log: deps.log,
    });
  },

  createPlugin(target): ReplayPlugin {
    return { name: "chance", actionHandlers: [new DetonateChanceSymbolActionHandler(target)] };
  },
};
